import {
  deleteMiscTask,
  deleteOrder,
  deletePatientCommunication,
  deleteVisit,
} from '../../services/api';
import type {
  BatchPrintTaskRef,
  ConflictCode,
  TaskListResponse,
} from '../../services/api/types.gen';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import type { ConfirmSpec } from '../SelectionActionBar';
import {
  getScheduleLockedTask,
  isNotesMutationLocked,
  type LockedTaskType,
} from './lockedTask';
import {
  getTaskRowClassification,
  TASK_TYPE_FILTER_TABS,
  type TaskTypeFilterValue,
} from './taskTypeFilters';

/**
 * Helpers backing the Episode Manager batch-actions bar and per-row delete
 * (ROG-2506). Eligibility, the task-type → delete-endpoint mapping, error-code
 * copy, and payload shaping live here so PatientDetail stays thin and these
 * decisions stay unit-testable — and so the row trash icon and the batch
 * runner cannot disagree on which endpoint a type hits.
 */

type TaskRef = Pick<TaskListResponse, 'task_type' | 'status' | 'imported_at'>;

/**
 * A task can be deleted only when it is not schedule-locked (signed / sent /
 * completed). Deletion is not task-type specific: every type, communications
 * included, is eligible while unlocked, matching the server, which refuses a
 * delete on locked status alone (EMR-533).
 *
 * Shared by the per-row trash icon and {@link partitionDeletable} so the two
 * cannot drift. Deleted-row gating stays at the call site: the interleaved
 * deleted view is a PatientDetail concern, not an eligibility rule.
 *
 * Schedule-locked imported rows stay undeletable unless the caller passes
 * `mayDeleteImported` for a `tasks:delete-imported` holder (EMR-3086); the
 * server enforces the same rule.
 */
export function canDeleteTask(
  task: TaskRef,
  mayDeleteImported = false
): boolean {
  if (getScheduleLockedTask(task) === null) {
    return true;
  }
  return mayDeleteImported && task.imported_at != null;
}

/**
 * Split a selection into rows {@link canDeleteTask} accepts and rows it
 * skips. Locked rows are surfaced so the confirm dialog can disclose them.
 */
export function partitionDeletable<T extends TaskRef>(
  tasks: T[],
  mayDeleteImported = false
): { deletable: T[]; skippedLocked: T[] } {
  const deletable: T[] = [];
  const skippedLocked: T[] = [];
  for (const task of tasks) {
    if (canDeleteTask(task, mayDeleteImported)) {
      deletable.push(task);
    } else {
      skippedLocked.push(task);
    }
  }
  return { deletable, skippedLocked };
}

/**
 * Keyed lookup rather than a ternary chain: an unmapped task type is a
 * compile error here instead of silently inheriting the visit endpoint
 * (which is how a communication delete used to 404 as a missing visit).
 */
const DELETE_TASK_BY_TYPE = {
  visit: (id: string) => deleteVisit({ path: { visit_id: id } }),
  order: (id: string) => deleteOrder({ path: { order_id: id } }),
  misc_task: (id: string) => deleteMiscTask({ path: { misc_task_id: id } }),
  patient_communication: (id: string) =>
    deletePatientCommunication({ path: { communication_id: id } }),
} satisfies Record<
  LockedTaskType,
  (id: string) => Promise<{ error?: unknown }>
>;

export function isDeletableTaskType(
  taskType: string
): taskType is LockedTaskType {
  return Object.prototype.hasOwnProperty.call(DELETE_TASK_BY_TYPE, taskType);
}

/**
 * Dispatch a delete to the endpoint that owns this task type. Unknown types
 * reject rather than falling through to the visit endpoint.
 */
export function deleteTaskRequest(
  task: Pick<TaskListResponse, 'id' | 'task_type'>
): Promise<{ error?: unknown }> {
  if (!isDeletableTaskType(task.task_type)) {
    return Promise.reject(
      new Error(`No delete endpoint for task type: ${task.task_type}`)
    );
  }
  return DELETE_TASK_BY_TYPE[task.task_type](task.id);
}

/**
 * Split a selection by whether work has begun. Not-started rows print as blank
 * template forms, which the print confirm gate discloses before exporting.
 */
export function partitionNotStarted<T extends Pick<TaskListResponse, 'status'>>(
  tasks: T[]
): { notStarted: T[]; started: T[] } {
  const notStarted: T[] = [];
  const started: T[] = [];
  for (const task of tasks) {
    if (task.status === 'not_started') {
      notStarted.push(task);
    } else {
      started.push(task);
    }
  }
  return { notStarted, started };
}

/**
 * Map a delete-failure {@link ConflictCode} to short, user-facing copy. The
 * server `detail` is intentionally never surfaced (see
 * `.cursor/rules/error-handling.mdc`); we key off the stable `code` instead.
 * Unknown / missing codes degrade to a generic phrase.
 */
const DEFAULT_DELETE_CONFLICT_COPY = "couldn't be deleted";

export function deleteConflictCopy(
  code?: ConflictCode | string | null
): string {
  switch (code) {
    case 'VISIT_LINKED_TO_ORDER':
      return "linked to an order that can't be unlinked automatically";
    case 'VISIT_SIGNATURE_REQUEST_SENT':
      return 'signature request already sent to the physician';
    case 'VISIT_LOCKED':
      return 'signed or completed';
    case 'ORDER_LOCKED':
      return 'signed or sent';
    case 'COMMUNICATION_LOCKED':
      return 'completed';
    case 'MISC_TASK_LOCKED':
      return 'completed';
    default:
      return DEFAULT_DELETE_CONFLICT_COPY;
  }
}

/**
 * Read a delete-failure {@link ConflictCode} out of a generated-client result.
 *
 * Takes the whole result, not its `error`, because the two halves it needs live
 * in different places: the status on `result.response`, the code in the
 * `result.error` body. Passing only the body drops the status, and the 409
 * check then fails for every response — every delete falls back to the generic
 * message and never says the order was sent or signed.
 *
 * The status check earns its place: only a 409 says something the user can act
 * on, so a 500 body that happens to carry a `code` must not be mapped to "this
 * is signed". The `typeof`/`null` guard is load-bearing rather than a bare
 * `in`: a proxy can put a non-JSON body here as a string, and `in` throws on a
 * primitive.
 */
export function deleteConflictCode(
  result: unknown
): ConflictCode | string | undefined {
  if (getHttpStatus(result) !== 409) return undefined;
  const body = (result as { error?: unknown } | null)?.error;
  if (typeof body !== 'object' || body === null) return undefined;
  if (!('code' in body)) return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * The same reasons as {@link deleteConflictCopy}, as a whole sentence for a
 * single-task toast. The fragments are adjectival ("signed or completed") so
 * they read after "is"; the fallback is already a verb phrase and takes the
 * bare subject instead.
 */
export function deleteConflictSentence(
  code?: ConflictCode | string | null
): string {
  const reason = deleteConflictCopy(code);
  return reason === DEFAULT_DELETE_CONFLICT_COPY
    ? `This task ${reason}.`
    : `This task is ${reason}.`;
}

/**
 * Split a selection by whether the bulk-notes endpoint will accept the row.
 *
 * Only a signed misc task is frozen: its notes are its content field and are
 * hashed into the signing snapshot. Visits, orders and communications keep an
 * editable sticky note even once they are schedule-locked, so they stay
 * eligible. The endpoint is all-or-nothing, so one frozen row would fail the
 * whole batch; they are dropped here and disclosed instead.
 */
export function partitionNoteable<
  T extends Pick<TaskListResponse, 'task_type' | 'status'>,
>(tasks: T[]): { noteable: T[]; skippedLockedMisc: T[] } {
  const noteable: T[] = [];
  const skippedLockedMisc: T[] = [];
  for (const task of tasks) {
    if (isNotesMutationLocked(task)) {
      skippedLockedMisc.push(task);
    } else {
      noteable.push(task);
    }
  }
  return { noteable, skippedLockedMisc };
}

/**
 * The disclosure line for rows {@link partitionNoteable} dropped, or null when
 * nothing was dropped. Extracted so the copy is testable without rendering the
 * dialog, matching {@link deleteConflictSentence}.
 */
export function lockedNoteSkipNotice(skippedCount: number): string | null {
  if (skippedCount <= 0) return null;
  return `${skippedCount} selected task${
    skippedCount === 1 ? ' is' : 's are'
  } signed and will be skipped.`;
}

/**
 * Task types that have a rendered PDF. Mirrors the backend
 * `BatchPrintTaskRef.task_type` literal, which rejects anything else with a 422,
 * so rows of any other type must be filtered out before the request is built.
 * Shared with the per-row print affordance (`hasPrintView` in `usePrintView`) so
 * the row button and the batch filter can never disagree.
 */
export const PRINTABLE_TASK_TYPES = new Set<BatchPrintTaskRef['task_type']>([
  'visit',
  'order',
  'patient_communication',
]);

/**
 * Split a selection by whether the task has a print view at all. Misc tasks have
 * no rendered document, so they are disclosed as skipped rather than sent to the
 * endpoint and rejected.
 */
export function partitionPrintable<
  T extends Pick<TaskListResponse, 'task_type'>,
>(tasks: T[]): { printable: T[]; skippedUnprintable: T[] } {
  const printable: T[] = [];
  const skippedUnprintable: T[] = [];
  for (const task of tasks) {
    if (
      PRINTABLE_TASK_TYPES.has(task.task_type as BatchPrintTaskRef['task_type'])
    ) {
      printable.push(task);
    } else {
      skippedUnprintable.push(task);
    }
  }
  return { printable, skippedUnprintable };
}

/** Shape the selected tasks into the batch-print endpoint's task refs. */
export function toBatchPrintTaskRefs(
  tasks: Pick<TaskListResponse, 'id' | 'task_type'>[]
): BatchPrintTaskRef[] {
  return partitionPrintable(tasks).printable.map(task => ({
    task_id: task.id,
    task_type: task.task_type as BatchPrintTaskRef['task_type'],
  }));
}

// The label shown for each manifest grouping. Therapy rows group by their
// specific discipline (PT/OT/ST) so the manifest reads as the clinician thinks,
// falling back to the generic "Therapy" tab label when the discipline is
// unknown.
const FILTER_LABEL_BY_VALUE: Record<TaskTypeFilterValue, string> =
  TASK_TYPE_FILTER_TABS.reduce(
    (acc, tab) => {
      acc[tab.value] = tab.label;
      return acc;
    },
    {} as Record<TaskTypeFilterValue, string>
  );

/**
 * The manifest grouping for a task: a stable `key` and the human label drawn
 * from {@link TASK_TYPE_FILTER_TABS}, so the bar's grouped manifest matches the
 * filter tabs the user already knows.
 */
export function taskManifestGroup(task: TaskListResponse): {
  key: string;
  label: string;
} {
  const classification = getTaskRowClassification(task);
  switch (classification.category) {
    case 'orders':
      return { key: 'orders', label: FILTER_LABEL_BY_VALUE.orders };
    case 'comm':
      return { key: 'comm', label: FILTER_LABEL_BY_VALUE.comm };
    case 'misc':
      return { key: 'misc', label: FILTER_LABEL_BY_VALUE.misc };
    case 'nursing':
      return { key: 'nursing', label: FILTER_LABEL_BY_VALUE.nursing };
    case 'msw':
      return { key: 'msw', label: FILTER_LABEL_BY_VALUE.msw };
    case 'hha':
      return { key: 'hha', label: FILTER_LABEL_BY_VALUE.hha };
    case 'therapy': {
      const discipline = classification.therapyDiscipline;
      if (discipline === 'PT') {
        return { key: 'pt', label: FILTER_LABEL_BY_VALUE.pt };
      }
      if (discipline === 'OT') {
        return { key: 'ot', label: FILTER_LABEL_BY_VALUE.ot };
      }
      if (discipline === 'ST') {
        return { key: 'st', label: FILTER_LABEL_BY_VALUE.st };
      }
      return { key: 'all_therapy', label: FILTER_LABEL_BY_VALUE.all_therapy };
    }
    default:
      return { key: 'other', label: 'Other' };
  }
}

/**
 * The confirm gate shared by both print actions.
 *
 * Returns null when there is nothing to disclose. Discloses not-started rows
 * (which print as blank templates) and rows with no printable document (which
 * are left out of the chart record). When nothing at all can print, it says so
 * and offers only a dismiss, because "Continue" would only reach the error path.
 */
export function buildPrintConfirm(
  items: TaskListResponse[]
): ConfirmSpec | null {
  const { notStarted } = partitionNotStarted(items);
  const { skippedUnprintable } = partitionPrintable(items);
  const blanks = notStarted.length;
  const unprintable = skippedUnprintable.length;
  if (blanks === 0 && unprintable === 0) return null;

  if (unprintable === items.length) {
    return {
      title: 'Nothing to print',
      body: 'The selected tasks have no printable document.',
      choices: [{ key: 'cancel', label: 'Close', variant: 'outline' }],
    };
  }

  const parts: string[] = [];
  if (blanks > 0) {
    parts.push(
      `${blanks} selected task${blanks === 1 ? '' : 's'} ${
        blanks === 1 ? "hasn't" : "haven't"
      } been started and will print as blank template forms.`
    );
  }
  if (unprintable > 0) {
    parts.push(
      `${unprintable} selected task${
        unprintable === 1 ? ' has' : 's have'
      } no printable document and will be left out of the chart record.`
    );
  }
  return {
    title:
      blanks > 0 ? "Some tasks haven't started" : 'Some tasks cannot print',
    body: parts.join(' '),
    choices: [
      { key: 'cancel', label: 'Cancel', variant: 'outline' },
      {
        key: 'completed',
        label: 'Print only completed',
        disabled: blanks === 0 || blanks === items.length,
      },
      { key: 'all', label: blanks > 0 ? 'Include blank forms' : 'Continue' },
    ],
  };
}
